import {
	createFileRoute,
	Link,
	useNavigate,
	useRouteContext,
	useSearch,
	stripSearchParams,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { cursorToOffset, offsetToCursor } from "graphql-relay";
import { useMemo, useTransition } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { z } from "zod";
import { Heading } from "@/components/heading";
import { Icon } from "@/components/icon";
import { Text } from "@/components/text";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/cn";
import type {
	leaderboard_pageQuery,
	leaderboard_pageQuery$data,
} from "./__generated__/leaderboard_pageQuery.graphql";
import { Skeleton } from "@/components/ui/skeleton";

const defaultValues = {
	page: 1,
	size: 10,
	orderBy: "current_month",
} as const;

export const Route = createFileRoute(
	"/_visibility/_topnav/ranking/leaderboard",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			page: z.number().default(1),
			size: z.number().default(10),
			orderBy: z
				.enum(["total", "current_month", "last_3_months", "last_month"])
				.default("current_month"),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
	pendingComponent: () => {
		return (
			<div className="w-full h-full flex items-center justify-center">
				<div className="flex flex-col w-full max-w-[900px] px-6 pb-6">
					<div className="flex max-sm:flex-col gap-6 flex-row items-baseline justify-between mb-6 mt-6">
						<Heading>Ranking</Heading>

						<Link to="/ranking/pontuacao">
							<Button variant="outline">Como é calculada a pontuação?</Button>
						</Link>
					</div>

					<Skeleton className="w-full h-[480px] mt-6 mb-6" />
				</div>
			</div>
		);
	},
	pendingMinMs: 100,
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "__root__" });
	const { page, size, orderBy } = useSearch({ from: Route.id });
	const navigate = useNavigate({ from: Route.fullPath });

	const orderByField = useMemo(() => {
		switch (orderBy) {
			case "last_3_months":
				return "TOTAL_POINTS_LAST_3_MONTHS";
			case "last_month":
				return "TOTAL_POINTS_LAST_MONTH";
			case "total":
				return "TOTAL_POINTS";
			default:
				return "TOTAL_POINTS_CURRENT_MONTH";
		}
	}, [orderBy]);
	const [_isPending, startTransition] = useTransition();

	const data = useLazyLoadQuery<leaderboard_pageQuery>(
		graphql`
		query leaderboard_pageQuery($slug: String!, $after: String, $first: Int!, $orderBy: RankingUserOrderBy, $order: SortOrder) {
			club(slug: $slug) {
			  ranking {
         	users(first: $first, after: $after, order: $order, orderBy: $orderBy) {
            edges {
            	node {
            		user {
            			displayName
          				username
          				photo {
          					signedUrl
          				}
             			avatarFallback
            		}
                totalPoints
                totalPointsCurrentMonth
                totalPointsLastMonth
                totalPointsLast3Months
          		}
            }
            pageInfo {
              startCursor
              endCursor
              totalCount
              hasNextPage
              hasPreviousPage
            }
          }
				}
			}
		}
	`,
		{
			slug: club?.slug || "",
			first: size,
			after: page > 1 ? offsetToCursor((page - 1) * size) : undefined,
			orderBy: orderByField,
			order: "DESC",
		},
	);

	const renderPosition = (position: number) => {
		if (position === 1) {
			return "🥇";
		} else if (position === 2) {
			return "🥈";
		} else if (position === 3) {
			return "🥉";
		}

		return position;
	};

	const renderPoints = (
		user: leaderboard_pageQuery$data["club"]["ranking"]["users"]["edges"][number],
	) => {
		switch (orderByField) {
			case "TOTAL_POINTS":
				return user.node.totalPoints;
			case "TOTAL_POINTS_CURRENT_MONTH":
				return user.node.totalPointsCurrentMonth;
			case "TOTAL_POINTS_LAST_3_MONTHS":
				return user.node.totalPointsLast3Months;
			case "TOTAL_POINTS_LAST_MONTH":
				return user.node.totalPointsLastMonth;
			default:
				return 0;
		}
	};

	return (
		<div className="w-full h-full flex items-center justify-center">
			<div className="flex flex-col w-full max-w-[900px] px-6 pb-6">
				<div className="flex max-sm:flex-col gap-6 flex-row items-baseline justify-between mb-6 mt-6">
					<Heading>Ranking</Heading>

					<Link to="/ranking/pontuacao">
						<Button variant="outline">Como é calculada a pontuação?</Button>
					</Link>
				</div>
				<Card className="gap-0">
					<div className="px-6 pb-6 border-b">
						<div className="flex flex-row items-center justify-start w-[300px]">
							<Select
								value={orderBy}
								onValueChange={(value) => {
									startTransition(() => {
										navigate({
											search: (s) => ({
												...s,
												orderBy: value as unknown as
													| "total"
													| "current_month"
													| "last_3_months"
													| "last_month",
											}),
										});
									});
								}}
							>
								<SelectTrigger className="w-[300px]">
									<SelectValue placeholder="Selecione o período" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="current_month">Mês Atual</SelectItem>
									<SelectItem value="last_month">Mês Passado</SelectItem>
									<SelectItem value="last_3_months">Ult. 3 meses</SelectItem>
									<SelectItem value="total">Desde o início</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>

					<div>
						{data.club.ranking.users.edges.map((user, index) => (
							<div
								key={user.node.user.username}
								className="flex flex-row gap-3 items-center justify-between p-6 py-4 border-b"
							>
								<div className="flex flex-1 flex-row items-center justify-start gap-3">
									<div
										className={cn(
											"w-8 flex items-center justify-center text-2xl leading-0 font-medium",
											index > 2 && "text-xl",
										)}
									>
										{renderPosition((page - 1) * 10 + index + 1)}
									</div>
									<Avatar>
										<AvatarImage src={user.node.user.photo?.signedUrl} />
										<AvatarFallback>
											{user.node.user.avatarFallback.toUpperCase()}
										</AvatarFallback>
									</Avatar>

									<span className="line-clamp-1 flex-1">
										{user.node.user.displayName}
									</span>
								</div>

								<div className="font-medium text-right text-xl">
									{renderPoints(user)}{" "}
									<span className="text-xs text-muted-foreground">pts</span>
								</div>
							</div>
						))}
					</div>

					{data.club.ranking.users.pageInfo.startCursor &&
					data.club.ranking.users.pageInfo.endCursor ? (
						<div className="p-6 pb-0 flex flex-row items-center justify-between max-sm:justify-end">
							<Text className="max-sm:hidden">
								Mostrando de{" "}
								{Math.max(
									cursorToOffset(data.club.ranking.users.pageInfo.startCursor),
									1,
								)}{" "}
								a{" "}
								{cursorToOffset(data.club.ranking.users.pageInfo.endCursor) + 1}{" "}
								de {data.club.ranking.users.pageInfo.totalCount} resultados
							</Text>

							<div className="flex flex-row gap-2">
								<Link
									from={Route.fullPath}
									search={(s) => ({ ...s, page: (s.page || 2) - 1 })}
								>
									<Button
										type="button"
										variant="outline"
										className="flex flex-row items-center justify-center"
										disabled={!data.club.ranking.users.pageInfo.hasPreviousPage}
									>
										<Icon name="ChevronLeft" className="size-4" />
										Anterior
									</Button>
								</Link>

								<Link
									from={Route.fullPath}
									search={(s) => ({ ...s, page: (s.page || 0) + 1 })}
								>
									<Button
										variant="outline"
										className="flex flex-row items-center justify-center"
										disabled={!data.club.ranking.users.pageInfo.hasNextPage}
									>
										Próximo
										<Icon name="ChevronRight" className="size-4" />
									</Button>
								</Link>
							</div>
						</div>
					) : null}
				</Card>
			</div>
		</div>
	);
}
